/* product-subnav.js
   Floating product sub-nav.
   - Bar is position: fixed. In 'scroll' mode (data-reveal) it slides in once
     the section's zero-height sentinel scrolls above the viewport, and slides
     out when scrolled back up. In 'atc' mode (the main-product copy) it shows
     once the theme's add-to-cart button is out of the viewport.
   - Fallback copy: the bar main-product renders carries data-sticky-fallback.
     If a Product sub-nav section is also on the page, the fallback removes
     itself so there is never more than one bar.
   - data-desktop="false" hides the bar at desktop widths (CSS); header
     hide/reveal is skipped while the bar is hidden.
   - Site header hides on scroll down and reveals on scroll up. The bar's top
     tracks the visible header height plus the gap setting.
   - Jump links are built from [data-subnav-label] anchors plus manual
     selector blocks, sorted by document order.
   - Link row: edge fades when it can scroll, and the active link is kept in
     view so nothing gets lost off the right edge.
   - Bar height for offset math is the COLLAPSED height: in menu mode
     (mobile) the expandable link list is subtracted, so it does not matter
     whether the menu is open, closed, or mid-animation when a link is tapped.
   - Jumps self-correct: once the scroll has stopped, the target is re-measured
     and nudged if anything shifted. Set window.SUBNAV_DEBUG = true to log.
   - Buy Now proxies to the theme's add-to-cart button. When the product form
     is not on the page (main product section disabled), it adds the variant
     rendered on the bar through the cart API and opens the cart drawer. */
(function () {
  'use strict';

  var ATC_FALLBACK = 'form[action*="/cart/add"] button[name="add"]';
  var PRICE_FALLBACK = '[data-product-price], #ProductPrice, .product__price';
  var HEADER_FALLBACK = '#shopify-section-header, .site-header, #SiteHeader';

  function dbg() {
    if (window.SUBNAV_DEBUG && window.console) console.log.apply(console, ['[subnav]'].concat([].slice.call(arguments)));
  }

  /* Is Impulse's cart drawer on screen? Checks the html/body class and the
     drawer's actual position, so it works whichever way the theme flags it. */
  function drawerOpen() {
    var root = document.documentElement, body = document.body;
    if (root.classList.contains('js-drawer-open') || body.classList.contains('js-drawer-open')) return true;
    var d = document.getElementById('CartDrawer');
    if (!d) return false;
    if (d.classList.contains('is-active') || d.classList.contains('drawer--is-open')) return true;
    var r = d.getBoundingClientRect();
    return r.width > 0 && r.left < window.innerWidth - 10 && getComputedStyle(d).visibility !== 'hidden';
  }

  function init(root) {
    if (!root || root.dataset.subnavReady === 'true') return;

    /* The main-product copy yields to a Product sub-nav section on the same page. */
    if (root.hasAttribute('data-sticky-fallback') &&
        document.querySelector('[data-product-subnav]:not([data-sticky-fallback])')) {
      dbg('fallback bar removed, section bar present');
      if (root.parentNode) root.parentNode.removeChild(root);
      return;
    }

    root.dataset.subnavReady = 'true';

    var sentinel = root.closest('.shopify-section') || root;
    var linksEl = root.querySelector('[data-subnav-links]');
    var toggle = root.querySelector('[data-subnav-toggle]');
    var buyBtn = root.querySelector('[data-subnav-buy]');
    var buyText = root.querySelector('[data-subnav-buy-text]');
    var priceEls = root.querySelectorAll('[data-subnav-price]');
    var compareEl = root.querySelector('[data-subnav-compare]');
    var extraOffset = parseInt(root.dataset.scrollOffset || '16', 10);
    var revealMode = root.dataset.reveal || 'scroll';
    var gap = readGap();
    var mobileMq = window.matchMedia('(max-width: 768px)');
    var reduceMq = window.matchMedia('(prefers-reduced-motion: reduce)');

    function readGap() {
      var g = parseInt(getComputedStyle(root).getPropertyValue('--subnav-gap-active'), 10);
      return isNaN(g) ? 12 : g;
    }

    /* Bar hidden by CSS (data-desktop="false" at desktop widths) */
    function barOff() {
      return getComputedStyle(root).display === 'none';
    }

    var HIDE_AFTER = 120; /* px from top before the header may hide */
    var DELTA = 6;        /* px of movement needed to flip direction */

    /* ---------- bar height (collapsed) ---------- */
    /* Menu mode = the chevron toggle is displayed, i.e. the link list is the
       expandable mobile menu rather than an inline row. In that mode the
       list's current height (open, closed, or animating) is subtracted. */
    function menuMode() {
      return !!toggle && getComputedStyle(toggle).display !== 'none';
    }
    function barHeight() {
      var h = root.getBoundingClientRect().height;
      if (menuMode() && linksEl) h -= linksEl.getBoundingClientRect().height;
      return Math.max(0, h);
    }

    /* ---------- header ---------- */
    function findHeader() {
      var custom = root.dataset.headerSelector;
      if (custom) {
        try { var c = document.querySelector(custom); if (c) return c; } catch (e) {}
      }
      return document.querySelector(HEADER_FALLBACK);
    }

    var headerEl = findHeader();
    var headerHidden = false;
    var headerPinned = false;
    var lastY = window.pageYOffset;
    var spacer = null;
    var themeStickyHeader = false; /* true when Impulse's own sticky header is on */
    var jumping = false;           /* true while a programmatic jump is in flight */

    if (headerEl) {
      headerEl.classList.add('subnav-header');
      var inner = headerEl.querySelector('.site-header') || headerEl;
      var ipos = getComputedStyle(inner).position;
      var opos = getComputedStyle(headerEl).position;
      themeStickyHeader = ipos === 'fixed' || ipos === 'sticky' || opos === 'fixed' || opos === 'sticky';
    }

    function headerHeight() {
      return headerEl ? headerEl.getBoundingClientRect().height : 0;
    }

    /* Height of header currently occupying the top of the viewport */
    function visibleHeaderHeight() {
      if (!headerEl || headerHidden) return 0;
      /* While pinned the header is mid-transition when this runs, so its
         rect is unreliable. Use the full height; the bar animates to it. */
      if (headerPinned) return headerHeight();
      var r = headerEl.getBoundingClientRect();
      if (r.bottom <= 0) return 0;
      return Math.max(0, r.bottom);
    }

    function pinHeader() {
      if (!headerEl || headerPinned) return;
      headerPinned = true;
      var h = headerHeight();
      /* The hide animation slides the header up by its own height via `top`
         (not transform, which would break the cart drawer rendered inside it). */
      headerEl.style.setProperty('--subnav-header-h', Math.ceil(h + 4) + 'px');
      /* Impulse already reserves space when its sticky header is on.
         When it is off, add a spacer so the page does not jump. */
      if (!themeStickyHeader) {
        spacer = document.createElement('div');
        spacer.className = 'subnav-header-spacer';
        spacer.style.height = h + 'px';
        headerEl.parentNode.insertBefore(spacer, headerEl);
      }
      headerEl.classList.add('subnav-header--pinned');
    }

    function unpinHeader() {
      if (!headerEl || !headerPinned) return;
      headerPinned = false;
      headerEl.classList.remove('subnav-header--pinned', 'subnav-header--hidden');
      headerHidden = false;
      if (spacer && spacer.parentNode) spacer.parentNode.removeChild(spacer);
      spacer = null;
      setOffsetVars();
    }

    function hideHeader() {
      if (!headerEl || headerHidden) return;
      pinHeader();
      headerHidden = true;
      headerEl.classList.add('subnav-header--hidden');
      setOffsetVars();
    }

    function showHeader() {
      if (!headerEl || !headerHidden) return;
      headerHidden = false;
      headerEl.classList.remove('subnav-header--hidden');
      setOffsetVars();
    }

    function updateHeader() {
      if (!headerEl || jumping) return;
      var y = window.pageYOffset;
      if (y <= HIDE_AFTER) {
        /* Near the top: hand the header back to the theme */
        unpinHeader();
      } else if (y - lastY > DELTA) {
        hideHeader();
      } else if (lastY - y > DELTA) {
        pinHeader();
        showHeader();
      }
      lastY = y;
    }

    /* ---------- offsets ---------- */
    function setOffsetVars() {
      var docStyle = document.documentElement.style;
      docStyle.setProperty('--subnav-top', (visibleHeaderHeight() + gap) + 'px');
      docStyle.setProperty('--subnav-h', barHeight() + 'px');
      docStyle.setProperty('--subnav-extra', extraOffset + 'px');
    }

    function totalOffset(assumeHeaderHidden) {
      var h;
      if (assumeHeaderHidden === undefined) h = visibleHeaderHeight();
      else h = assumeHeaderHidden ? 0 : headerHeight();
      return h + gap + barHeight() + extraOffset;
    }

    /* ---------- bar reveal ---------- */
    var atcShown = false;
    function updateVisibility() {
      var show;
      if (revealMode === 'atc' && themeAtc) {
        /* Appear once the page is scrolled and the theme's add-to-cart button
           is off screen, then stay until the page is back at the top. No
           hide-and-return while scrolling past the real button. */
        var y = window.pageYOffset;
        if (y <= 0) {
          show = false;
        } else if (atcShown) {
          show = true;
        } else {
          var r = themeAtc.getBoundingClientRect();
          show = !(r.bottom > 0 && r.top < window.innerHeight);
        }
        atcShown = show;
      } else {
        show = sentinel.getBoundingClientRect().top < 0;
      }
      root.dataset.visible = show ? 'true' : 'false';
      if (!show) closeMenu();
    }

    /* ---------- link row overflow: edge fades + keep active in view ---------- */
    function updateFades() {
      if (!linksEl || mobileMq.matches) { if (linksEl) delete linksEl.dataset.fade; return; }
      var max = linksEl.scrollWidth - linksEl.clientWidth;
      var canR = max - linksEl.scrollLeft > 2;
      var canL = linksEl.scrollLeft > 2;
      linksEl.dataset.fade = canL && canR ? 'both' : canR ? 'right' : canL ? 'left' : 'none';
    }

    function revealLink(a) {
      if (!a || !linksEl || mobileMq.matches) return;
      var max = linksEl.scrollWidth - linksEl.clientWidth;
      if (max <= 0) return;
      var pad = 24;
      var left = a.offsetLeft - linksEl.offsetLeft;
      var right = left + a.offsetWidth;
      var viewL = linksEl.scrollLeft;
      var viewR = viewL + linksEl.clientWidth;
      var target = null;
      if (left - pad < viewL) target = left - pad;
      else if (right + pad > viewR) target = right + pad - linksEl.clientWidth;
      if (target === null) return;
      linksEl.scrollLeft = Math.max(0, Math.min(max, target));
    }

    /* ---------- collect targets ---------- */
    function collectTargets() {
      var items = [];

      document.querySelectorAll('[data-subnav-label]').forEach(function (el) {
        if (root.contains(el)) return;
        items.push({ label: el.dataset.subnavLabel, el: el, id: el.id || null });
      });

      root.querySelectorAll('[data-subnav-manual]').forEach(function (tpl) {
        var sel = tpl.dataset.selector;
        var el = null;
        try { el = document.querySelector(sel); } catch (e) { el = null; }
        if (!el) return;
        items.push({ label: tpl.dataset.label, el: el, id: el.id || null });
      });

      items.sort(function (a, b) {
        var pos = a.el.compareDocumentPosition(b.el);
        if (pos & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
        if (pos & Node.DOCUMENT_POSITION_PRECEDING) return 1;
        return 0;
      });

      return items;
    }

    /* ---------- render links ---------- */
    var targets = [];
    var links = [];
    var activeIndex = -1;

    function render() {
      targets = collectTargets();
      links = [];
      linksEl.innerHTML = '';

      targets.forEach(function (t) {
        var a = document.createElement('a');
        a.className = 'product-subnav__link';
        a.href = t.id ? '#' + t.id : '#';
        a.textContent = t.label;
        a.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          closeMenu();
          scrollTo(t.el, t.id, t.label);
        });
        linksEl.appendChild(a);
        links.push(a);
      });

      if (toggle) toggle.hidden = targets.length === 0;
      updateFades();
    }

    var jumpToken = 0;
    function scrollTo(el, id, label) {
      var current = window.pageYOffset;
      var goingDown = el.getBoundingClientRect().top > 0;
      var willHide = goingDown && current > HIDE_AFTER;
      var token = ++jumpToken;
      jumping = true;
      if (willHide) hideHeader(); else showHeader();

      /* Measured AFTER the header change so any spacer it inserted is included. */
      function targetTop() {
        return Math.max(0, el.getBoundingClientRect().top + window.pageYOffset - totalOffset(willHide));
      }

      var reduce = reduceMq.matches;
      var top = targetTop();
      var startY = window.pageYOffset;
      dbg('jump', label, { anchorTop: el.getBoundingClientRect().top + window.pageYOffset, offset: totalOffset(willHide), barH: barHeight(), header: willHide ? 0 : headerHeight(), target: top });
      window.scrollTo({ top: top, behavior: reduce ? 'auto' : 'smooth' });
      lastY = top;
      if (id && history.replaceState) history.replaceState(null, '', '#' + id);

      /* Settle: wait until the scroll has started AND stopped moving, then
         re-measure and nudge (instant) if anything shifted during the ride. */
      var tries = 0;
      var prevY = -1;
      var stable = 0;
      var polls = 0;
      function poll() {
        if (token !== jumpToken) return; /* a newer jump took over */
        var y = window.pageYOffset;
        var moved = Math.abs(y - startY) > 1 || Math.abs(top - startY) <= 1;
        if (Math.abs(y - prevY) < 1 && (moved || polls > 12)) stable++; else stable = 0;
        prevY = y;
        polls++;
        if (stable < 2 && polls < 60) { setTimeout(poll, 60); return; }
        var t = targetTop();
        if (Math.abs(y - t) > 4 && tries < 4) {
          tries++;
          dbg('settle', tries, { at: y, want: t });
          lastY = t;
          window.scrollTo({ top: t, behavior: 'auto' });
          startY = y; stable = 0; prevY = -1; polls = 0;
          setTimeout(poll, 60);
          return;
        }
        lastY = window.pageYOffset;
        jumping = false;
      }
      setTimeout(poll, reduce ? 30 : 120);
    }

    /* ---------- active state ---------- */
    var ticking = false;
    function onFrame() {
      ticking = false;
      if (barOff()) {
        /* Bar hidden at this width: leave the header to the theme. */
        unpinHeader();
        lastY = window.pageYOffset;
        return;
      }
      updateHeader();
      if (!headerPinned) setOffsetVars();
      updateVisibility();
      if (!targets.length) return;
      var line = totalOffset() + 1;
      var next = -1;
      for (var i = 0; i < targets.length; i++) {
        if (targets[i].el.getBoundingClientRect().top <= line) next = i;
      }
      if (next !== activeIndex) {
        activeIndex = next;
        links.forEach(function (a, i) {
          a.classList.toggle('is-active', i === activeIndex);
        });
        if (activeIndex >= 0) revealLink(links[activeIndex]);
      }
    }
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(onFrame);
    }

    /* ---------- mobile toggle ---------- */
    function openMenu() {
      root.dataset.open = 'true';
      if (toggle) toggle.setAttribute('aria-expanded', 'true');
    }
    function closeMenu() {
      if (root.dataset.open !== 'true') return;
      root.dataset.open = 'false';
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
    }
    if (toggle) {
      toggle.addEventListener('click', function () {
        root.dataset.open === 'true' ? closeMenu() : openMenu();
      });
    }
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeMenu();
    });

    /* ---------- theme hooks ---------- */
    function findAtc() {
      var custom = root.dataset.atcSelector;
      if (custom) {
        try { var c = document.querySelector(custom); if (c) return c; } catch (e) {}
      }
      return document.querySelector(ATC_FALLBACK);
    }

    function findPriceEl() {
      var custom = root.dataset.priceSelector;
      if (custom) {
        try { var c = document.querySelector(custom); if (c) return c; } catch (e) {}
      }
      return document.querySelector(PRICE_FALLBACK);
    }

    var themePrice = findPriceEl();
    var themeAtc = findAtc();

    if (buyText) buyText.dataset.orig = buyText.textContent;

    function syncFromTheme() {
      if (themePrice) {
        var txt = themePrice.textContent.replace(/\s+/g, ' ').trim();
        if (txt) {
          priceEls.forEach(function (p) { p.textContent = txt; });
          if (compareEl) compareEl.hidden = true;
        }
      }
      if (themeAtc && buyBtn) {
        var disabled = themeAtc.disabled || themeAtc.getAttribute('aria-disabled') === 'true';
        buyBtn.disabled = disabled;
        if (buyText) buyText.textContent = disabled ? 'Sold out' : buyText.dataset.orig;
      }
    }

    if (themePrice && 'MutationObserver' in window) {
      new MutationObserver(syncFromTheme).observe(themePrice, { childList: true, characterData: true, subtree: true });
    }
    if (themeAtc && 'MutationObserver' in window) {
      new MutationObserver(syncFromTheme).observe(themeAtc, { attributes: true, attributeFilter: ['disabled', 'aria-disabled'] });
    }

    /* ---------- Buy Now ---------- */
    /* No product form on the page: add the variant the bar was rendered with,
       hand the line item to Impulse's cart drawer, and only go to /cart if the
       drawer does not open. */
    function fallbackAdd() {
      var form = document.querySelector('form[action*="/cart/add"]');
      var idInput = form && form.querySelector('[name="id"]');
      var variantId = (idInput && idInput.value) || root.dataset.variantId;
      if (!variantId) { window.location.href = '/cart'; return; }
      buyBtn.classList.add('is-busy');
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 })
      })
        .then(function (r) { if (!r.ok) throw new Error('add failed'); return r.json(); })
        .then(function (item) {
          buyBtn.classList.remove('is-busy');
          try { document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: item, addToCartBtn: buyBtn } })); } catch (e) {}
          try { if (window.theme && theme.cartDrawer && typeof theme.cartDrawer.open === 'function') theme.cartDrawer.open(); } catch (e) {}
          setTimeout(function () { if (!drawerOpen()) window.location.href = '/cart'; }, 900);
        })
        .catch(function () { buyBtn.classList.remove('is-busy'); window.location.href = '/cart'; });
    }

    if (buyBtn) {
      buyBtn.addEventListener('click', function () {
        var atc = findAtc();
        if (atc && !atc.disabled) { atc.click(); return; }
        fallbackAdd();
      });
    }

    /* ---------- boot ---------- */
    setOffsetVars();
    render();
    onFrame();

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', function () { gap = readGap(); setOffsetVars(); onFrame(); updateFades(); }, { passive: true });
    if (linksEl) linksEl.addEventListener('scroll', updateFades, { passive: true });
    /* Fonts loading can change link widths after first paint */
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { updateFades(); setOffsetVars(); });

    if (window.Shopify && Shopify.designMode) {
      ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'].forEach(function (evt) {
        document.addEventListener(evt, function () { setOffsetVars(); render(); onFrame(); });
      });
    }

    if (location.hash) {
      var target = null;
      try { target = document.querySelector(location.hash); } catch (e) {}
      if (target && target.hasAttribute('data-subnav-label')) {
        setTimeout(function () { scrollTo(target, target.id, 'hash'); }, 50);
      }
    }
  }

  function boot() {
    /* Section-rendered bars first, so a fallback copy can see them and bow out. */
    var all = [].slice.call(document.querySelectorAll('[data-product-subnav]'));
    all.sort(function (a, b) {
      var fa = a.hasAttribute('data-sticky-fallback') ? 1 : 0;
      var fb = b.hasAttribute('data-sticky-fallback') ? 1 : 0;
      return fa - fb;
    });
    all.forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
